import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from './api-client';
import { useAuthStore } from '@/stores/authStore';

export interface Workspace {
  id: string;
  name: string;
  slug: string;
}

interface MeResponseV2 {
  user: {
    id: string;
    email: string;
    displayName: string | null;
    isAdmin: boolean;
  };
  memberships: Array<{
    workspaceId: string;
    workspaceName: string;
    role: string;
  }>;
}

interface WorkspaceContextValue {
  workspaces: Workspace[];
  currentWorkspace: Workspace | null;
  setCurrentWorkspace: (id: string) => void;
  isLoading: boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const WS_KEY = 'current_workspace_id';

export function WorkspaceProvider({ children }: { children: ReactNode }) {
  const isAuthenticated = !!useAuthStore((s) => s.user);

  const { data, isLoading } = useQuery<MeResponseV2>({
    queryKey: ['me'],
    queryFn: () => apiClient.get<MeResponseV2>('/v1/auth/v2/me').then((r) => r.data),
    enabled: isAuthenticated,
    staleTime: 5 * 60_000,
  });

  // V2 retourne `memberships` (pas workspaces). Map workspaceId→id, workspaceName→name,
  // slug = workspaceId (V2 me ne retourne pas slug, frontend l'utilise pour les VMs uniquement).
  const workspaces: Workspace[] = (data?.memberships ?? []).map((m) => ({
    id: m.workspaceId,
    name: m.workspaceName,
    slug: m.workspaceId,
  }));

  const [currentId, setCurrentId] = useState<string | null>(
    () => localStorage.getItem(WS_KEY),
  );

  // Auto-select first workspace when workspaces load and none is selected
  useEffect(() => {
    if (workspaces.length > 0 && !currentId) {
      const first = workspaces[0];
      if (!first) return;
      localStorage.setItem(WS_KEY, first.id);
      setCurrentId(first.id);
    }
  }, [workspaces, currentId]);

  const setCurrentWorkspace = useCallback((id: string) => {
    localStorage.setItem(WS_KEY, id);
    setCurrentId(id);
  }, []);

  const currentWorkspace = useMemo(
    () => workspaces.find((w) => w.id === currentId) ?? workspaces[0] ?? null,
    [workspaces, currentId],
  ) as Workspace | null;

  const value = useMemo<WorkspaceContextValue>(
    () => ({ workspaces, currentWorkspace, setCurrentWorkspace, isLoading }),
    [workspaces, currentWorkspace, setCurrentWorkspace, isLoading],
  );

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useCurrentWorkspace(): WorkspaceContextValue {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useCurrentWorkspace must be used within WorkspaceProvider');
  return ctx;
}
